/**
 * Horizontal / vertical step rail for AI Lead Intake wizard.
 * Mobile uses wrapping chips to avoid horizontal page scroll.
 */

import React from "react";
import {
  Box,
  Chip,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * @param {{
 *   steps: Array<{ id: string, label: string, shortLabel?: string }>,
 *   activeStepId: string,
 *   completedStepIds?: string[],
 *   onStepChange?: (stepId: string) => void,
 *   orientation?: 'horizontal'|'vertical',
 * }} props
 */
const IntakeStepRail = ({
  steps = [],
  activeStepId,
  completedStepIds = [],
  onStepChange,
  orientation,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const resolvedOrientation =
    orientation || (isMobile ? "horizontal" : "horizontal");
  const activeIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === activeStepId)
  );
  const completed = new Set(completedStepIds);

  if (isMobile) {
    return (
      <Box
        data-testid="intake-step-rail"
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          py: 1,
        }}
      >
        <Box
          component="nav"
          aria-label="Intake steps"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            width: "100%",
          }}
        >
          {steps.map((step, index) => {
            const isComplete = completed.has(step.id);
            const isActive = step.id === activeStepId;
            const label = step.shortLabel || step.label;
            return (
              <Chip
                key={step.id}
                clickable
                onClick={() => onStepChange?.(step.id)}
                color={isActive ? "primary" : isComplete ? "success" : "default"}
                variant={isActive || isComplete ? "filled" : "outlined"}
                icon={
                  isComplete ? (
                    <CheckCircleIcon
                      fontSize="small"
                      data-testid={`step-complete-${step.id}`}
                    />
                  ) : undefined
                }
                label={`${index + 1}. ${label}`}
                data-testid={`step-chip-${step.id}`}
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  py: 0.75,
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    display: "block",
                    lineHeight: 1.25,
                  },
                }}
              />
            );
          })}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.75 }}
        >
          Step {activeIndex + 1} of {steps.length}
          {completed.has(activeStepId) ? " · Confirmed" : ""}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="intake-step-rail"
      sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden", py: 1 }}
    >
      <Stepper
        nonLinear
        activeStep={activeIndex}
        orientation={resolvedOrientation}
        alternativeLabel={resolvedOrientation === "horizontal"}
        sx={{
          width: "100%",
          maxWidth: "100%",
          "& .MuiStepLabel-label": { wordBreak: "break-word" },
        }}
      >
        {steps.map((step) => {
          const isComplete = completed.has(step.id);
          const label = step.label;
          return (
            <Step key={step.id} completed={isComplete}>
              <StepButton
                onClick={() => onStepChange?.(step.id)}
                optional={
                  isComplete ? (
                    <Typography variant="caption" color="success.main">
                      Confirmed
                    </Typography>
                  ) : step.id === activeStepId ? (
                    <Typography variant="caption" color="primary.main">
                      Review
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Pending
                    </Typography>
                  )
                }
              >
                <StepLabel
                  StepIconComponent={
                    isComplete
                      ? () => (
                          <CheckCircleIcon
                            color="success"
                            fontSize="small"
                            data-testid={`step-complete-${step.id}`}
                          />
                        )
                      : undefined
                  }
                >
                  {label}
                </StepLabel>
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default IntakeStepRail;
